/**
 * Utility functions for handling EMR field values
 * Single Responsibility: Field value operations (get, set, defaults)
 */

export function getEmrFieldValue({ subSectionDataValues, fieldCode }) {
  return subSectionDataValues?.[fieldCode] || null
}

/**
 * Get field value from item based on field type
 */
export const getFieldValue = ({ subSectionDataValues, fieldCode, fieldType, subSection }) => {
  const emrValue = getEmrFieldValue({ subSectionDataValues, fieldCode })
  if (!emrValue) return getDefaultValue(fieldCode, fieldType, subSection)

  switch (fieldType) {
    case 'TEXT':
    case 'TEXTAREA':
      return emrValue.valueString || ''
    case 'NUMBER':
      return emrValue.valueNumber ?? ''
    case 'BOOLEAN':
      return emrValue.valueBoolean ?? false
    case 'DATE':
    case 'TIME':
    case 'DATETIME':
      return emrValue.valueDate || null
    case 'SELECT_ONE':
      return emrValue.code || ''
    case 'SELECT_MULTI':
      return emrValue.valueStringList || []
    default:
      return ''
  }
}

/**
 * Get default value for a field type
 */
export const getDefaultValue = ({ fieldCode, fieldType, subSection }) => {
  const field = subSection?.fields?.find((f) => f.code === fieldCode)
  if (!field) return null

  switch (fieldType) {
    case 'TEXT':
    case 'TEXTAREA':
      return field.defaultString || ''
    case 'NUMBER':
      return field.defaultNumber ?? ''
    case 'BOOLEAN':
      return field.defaultBoolean ?? false
    case 'DATE':
    case 'TIME':
    case 'DATETIME':
      return field.defaultDate || null
    case 'SELECT_ONE':
      return field.defaultCode || ''
    case 'SELECT_MULTI':
      return []
    default:
      return null
  }
}

/**
 * Create EMR value object from field value and type
 */
export const createEmrValue = (value, fieldType, displayValue = null, fieldLabel = null) => {
  const emrValue = {}

  switch (fieldType) {
    case 'TEXT':
    case 'TEXTAREA':
      emrValue.valueString = value
      break
    case 'NUMBER':
      emrValue.valueNumber = value === '' ? null : Number(value)
      break
    case 'BOOLEAN':
      emrValue.valueBoolean = value
      break
    case 'DATE':
    case 'TIME':
    case 'DATETIME':
      emrValue.valueDate = value
      break
    case 'SELECT_ONE':
      emrValue.code = value
      emrValue.display = displayValue
      break
    case 'SELECT_MULTI':
      // For QUERY/HYBRID, value is already an array of EmrValueItem objects
      // For STATIC/VALUE_SET, value is an array of codes
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
        emrValue.valueStringList = value // Already EmrValueItem[]
      } else {
        emrValue.valueStringList = value // Array of codes (STATIC/VALUE_SET)
      }
      break
  }

  // Save field label as valueLabel (field input label)
  if (fieldLabel) {
    emrValue.valueLabel = fieldLabel
  }

  return emrValue
}
